// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Capability affordances for administration screens.
 *
 * **This module is not an authorization mechanism.** `CST-048`, `ADM-014`, and
 * `07-SECURITY.md` §15.4 are explicit: the UI may improve usability by hiding
 * unavailable actions, but the server enforces every decision independently.
 * A user who defeats these checks — by editing memory, replaying a route, or
 * calling the API directly — gains nothing, because the BFF and the services
 * behind it authorize each request on their own.
 *
 * Capability strings are opaque identifiers issued by the BFF from the
 * verified session. The UI never derives one from a role name, a URL, or a
 * model response (`SEC-016`: no identity or permission by prompt).
 */

import type { SessionView } from '../api-clients/views';

/** Capability identifiers the administration applications reference. */
export const capabilities = {
  tenantsRead: 'control_plane.tenants.read',
  // ADR-0075: registering a college. Separate from the read capability above,
  // because an operator who may list colleges must not thereby create one.
  tenantsCreate: 'control_plane.tenants.create',
  // --- the college lifecycle (ADR-0076) -----------------------------------
  //
  // Five identifiers, five operations, one each. There is no
  // `control_plane.tenants.manage` and there must never be: Platform split
  // these scopes precisely because holding one is not evidence for holding
  // another, and a screen that checked a composite would offer an operator a
  // control the server would then refuse.
  //
  // Nor is any of them derived from a role. `university_admin` appears nowhere
  // in this module: a role that conferred capabilities by name would confer
  // every capability added after it, to every session already holding it. The
  // BFF's session record lists these individually and this table matches it
  // literally.
  tenantsUpdate: 'control_plane.tenants.update',
  tenantsDelete: 'control_plane.tenants.delete',
  tenantsDeactivate: 'control_plane.tenants.deactivate',
  tenantsActivationRotate: 'control_plane.tenants.activation.rotate',
  tenantsOnboardingRead: 'control_plane.tenants.onboarding.read',
  /** Approving an Edge appliance: its own authority, not part of update. */
  tenantsEdgeProvision: 'control_plane.tenants.edge.provision',
  aiServicesRead: 'control_plane.ai_services.read',
  // --- University AI settings (ADR-0080) ----------------------------------
  //
  // Six identifiers, six operations, one each -- the same rule as the college
  // lifecycle above. `credentialsWrite` is separate from the rest because
  // naming a model and holding the key that pays for it are different
  // authorities, and `providersWrite` is separate from `modelsWrite` because a
  // provider is the account the estate is billed through.
  aiSettingsRead: 'control_plane.ai_settings.read',
  aiSettingsProvidersWrite: 'control_plane.ai_settings.providers.write',
  aiSettingsModelsWrite: 'control_plane.ai_settings.models.write',
  aiSettingsCredentialsWrite: 'control_plane.ai_settings.credentials.write',
  aiSettingsRoutingWrite: 'control_plane.ai_settings.routing.write',
  aiSettingsProvidersTest: 'control_plane.ai_settings.providers.test',
  // ADR-0080, the cost dashboard. Separate from every settings capability
  // above: reading which models are configured and reading what every college
  // spent are different questions with different audiences.
  aiUsageRead: 'control_plane.ai_usage.read',
  knowledgeRead: 'control_plane.knowledge.read',
  documentsRead: 'control_plane.documents.read',
  governanceRead: 'control_plane.governance.read',
  governanceApprove: 'control_plane.governance.approve',
  analyticsRead: 'control_plane.analytics.read',
  auditRead: 'control_plane.audit.read',
  monitoringRead: 'control_plane.monitoring.read',
  accessRead: 'control_plane.access.read',
  memoryRead: 'control_plane.memory.read',
  tenantConnectorsRead: 'tenant.connectors.read',
  tenantDataSourcesRead: 'tenant.data_sources.read',
  tenantSchemaMappingRead: 'tenant.schema_mapping.read',
  tenantDocumentsRead: 'tenant.documents.read',
  tenantKnowledgeRead: 'tenant.knowledge.read',
  tenantPoliciesRead: 'tenant.policies.read',
  tenantAuditRead: 'tenant.audit.read',
  tenantHealthRead: 'tenant.health.read',
} as const;

/** A capability identifier referenced by the applications. */
export type Capability = (typeof capabilities)[keyof typeof capabilities];

/**
 * Report whether the session was offered a capability.
 *
 * Fails closed: an unresolved or unauthenticated session offers nothing
 * (`SEC-003`, deny by default on missing or ambiguous context).
 *
 * @param session - Session view, or `undefined` before it resolves.
 * @param capability - Capability identifier to test.
 * @returns `true` only when the verified session listed the capability.
 */
export function hasCapability(session: SessionView | undefined, capability: string): boolean {
  if (session?.authenticated !== true) {
    return false;
  }
  return session.capabilities.includes(capability);
}

/**
 * Report whether the session was offered every listed capability.
 *
 * @param session - Session view, or `undefined` before it resolves.
 * @param required - Capabilities that must all be present.
 * @returns `true` only when every capability is present.
 */
export function hasEveryCapability(
  session: SessionView | undefined,
  required: readonly string[],
): boolean {
  return required.every((capability) => hasCapability(session, capability));
}
